import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './Icon.js';

export interface ActionLinkProps {
  to: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'text';
  /** Layers the shared brass 9-slice face behind a `primary`/`secondary` link (see `Button`'s `emphasis`). */
  emphasis?: 'flat' | 'decorated';
  showArrow?: boolean;
  className?: string;
}

const VARIANTS: Record<NonNullable<ActionLinkProps['variant']>, string> = {
  primary: 'border-accent bg-interactive text-textOnBrand shadow-1 hover:bg-interactiveHover',
  secondary: 'border-frame bg-frame text-textOnDark shadow-1 hover:bg-frameElevated',
  text: 'border-transparent bg-transparent text-link hover:bg-surfaceAlt hover:text-linkHover',
};

/** Dark 9-slice art under the label in both variants — see `Button`'s `DECORATED_CLASSES`. */
const DECORATED: Partial<Record<NonNullable<ActionLinkProps['variant']>, string>> = {
  primary: 'px-btn-decorated px-btn-decorated--primary !text-textOnDark',
  secondary: 'px-btn-decorated px-btn-decorated--secondary !text-textOnDark',
};

export function ActionLink({
  to,
  children,
  variant = 'text',
  emphasis = 'flat',
  showArrow = false,
  className = '',
}: ActionLinkProps) {
  const decorated = emphasis === 'decorated' ? (DECORATED[variant] ?? '') : '';
  return (
    <Link
      to={to}
      className={`interactive-lift inline-flex min-h-touch items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${VARIANTS[variant]} ${decorated} ${className}`}
    >
      {children}
      {showArrow && <Icon name="arrowRight" className="h-4 w-4" />}
    </Link>
  );
}
